import { BaseQueryFn } from "@reduxjs/toolkit/query"
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios"
import { isTokenExpired } from "@/helpers/jwt"
import { captureException } from "@/helpers/sentry"

const getApiBaseUrl = () => {
  return import.meta.env.VITE_BACKEND
}

const getAccessToken = (): string | null => {
  const token = sessionStorage.getItem("accessToken")
  if (token) {
    return token
  }

  const oldToken = localStorage.getItem("accessToken")
  if (oldToken) {
    sessionStorage.setItem("accessToken", oldToken)
    localStorage.removeItem("accessToken")
    return oldToken
  }

  return null
}

const getRefreshToken = (): string | null => {
  return localStorage.getItem("refreshToken")
}

export const axiosInstance = axios.create({
  baseURL: `${getApiBaseUrl()}/api`,
  headers: {
    "Content-Type": "application/json"
  },
  withCredentials: true
})

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    if (refreshToken) {
      config.headers["x-refresh-token"] = refreshToken
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

type AppStore = {
  dispatch: (action: unknown) => void
}
type LogoutAction = (value: boolean) => { type: string; payload: boolean }

let appStore: AppStore | null = null
let logoutAction: LogoutAction | null = null

export const injectStore = (store: AppStore, action: LogoutAction) => {
  appStore = store
  logoutAction = action
}

export const setAuthHeader = (accessToken: string, refreshToken: string) => {
  sessionStorage.setItem("accessToken", accessToken)
  localStorage.setItem("refreshToken", refreshToken)

  axiosInstance.defaults.headers["Authorization"] = `Bearer ${accessToken}`
  axiosInstance.defaults.headers["x-refresh-token"] = refreshToken
}

export const clearAuthHeaders = () => {
  sessionStorage.removeItem("accessToken")
  localStorage.removeItem("refreshToken")
  delete axiosInstance.defaults.headers["Authorization"]
  delete axiosInstance.defaults.headers["x-refresh-token"]
}

const handleAuthError = () => {
  clearAuthHeaders()
  if (appStore && logoutAction) {
    appStore.dispatch(logoutAction(false))
  }
}

let refreshPromise: Promise<string> | null = null
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve()
    }
  })
  failedQueue = []
}

const refreshTokens = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const { data } = await axiosInstance.post<{
        accessToken: string
        refreshToken: string
      }>("/auth/refresh", undefined, {
        skipAuthRefresh: true
      } as InternalAxiosRequestConfig & { skipAuthRefresh?: boolean })
      setAuthHeader(data.accessToken, data.refreshToken)
      return data.accessToken
    } catch (error) {
      handleAuthError()
      throw error
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export const refreshTokensIfNeeded = async (): Promise<boolean> => {
  const accessToken = getAccessToken()
  if (!accessToken) {
    return false
  }

  if (isTokenExpired(accessToken)) {
    try {
      await refreshTokens()
      return true
    } catch {
      return false
    }
  }

  return false
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      skipAuthRefresh?: boolean
    }

    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error)
    }

    const isAuthEndpoint =
      originalRequest?.url?.includes("/auth/refresh") ||
      originalRequest?.url?.includes("/auth/telegram") ||
      originalRequest?.url?.includes("/auth/web") ||
      originalRequest?.url?.includes("/auth/email")

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !isAuthEndpoint
    ) {
      if (refreshPromise) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            const accessToken = getAccessToken()
            if (accessToken && originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
            return axiosInstance(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true

      try {
        await refreshTokens()
        processQueue(null)
        const accessToken = getAccessToken()
        if (accessToken && originalRequest) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }
        return axiosInstance(originalRequest)
      } catch (refreshError) {
        captureException(refreshError, {
          context: "Token refresh failed"
        })
        processQueue(refreshError)
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

interface AxiosBaseQueryArgs {
  url: string
  headers?: Record<string, string>
  method: string
  data?: unknown
  params?: unknown
}

export const axiosBaseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> =>
  async ({ url, headers, method, data, params }) => {
    const isFormData = data instanceof FormData

    try {
      const result = await axiosInstance({
        url,
        headers: {
          "Content-Type": isFormData
            ? "multipart/form-data"
            : "application/json",
          ...headers
        },
        method,
        data,
        params
      })
      return { data: result?.data }
    } catch (error: unknown) {
      const axiosError = error as AxiosError

      const status = axiosError.response?.status
      captureException(error, {
        url,
        method,
        status,
        responseData: axiosError.response?.data
      })

      return { error }
    }
  }
